import { create } from "zustand";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";

export interface CommissionStatusEvent {
  status: "Pending" | "Locked" | "Paid";
  at: string;
  by?: { fullName?: string; email?: string } | string | null;
  note?: string;
}

export interface Commission {
  _id: string;
  businessDeveloper?: { _id: string; fullName: string; email?: string } | null;
  project?: { _id: string; name: string; projectType?: string; source?: string } | null;
  client?: { _id: string; name: string; email?: string; company?: string } | null;
  milestoneIndex: number;
  milestoneTitle: string;
  releasedAmount: number;
  ratePkrPerUsd: number;
  ruleText: string;
  commissionAmount: number;
  status: "Pending" | "Locked" | "Paid";
  payrollBatch?: string;
  payrollRef?: string;
  processedAt?: string | null;
  processedBy?: { fullName?: string; email?: string } | null;
  statusHistory?: CommissionStatusEvent[];
  createdAt: string;
  updatedAt: string;
}

export interface CommissionKpis {
  pending: number;
  locked: number;
  paid: number;
  totalEarned: number;
  count: number;
}

export type CommissionFilters = {
  from?: string;
  to?: string;
  status?: string;
  bdId?: string;
  client?: string;
  search?: string;
};

interface State {
  commissions: Commission[];
  kpis: CommissionKpis | null;
  loading: boolean;
  fetchCommissions: (f?: CommissionFilters) => Promise<void>;
  fetchKpis: (f?: CommissionFilters) => Promise<void>;
  getCommission: (id: string) => Promise<Commission | null>;
  processPayroll: (id: string, batch?: string) => Promise<boolean>;
}

export const useCommissionsStore = create<State>((set, get) => ({
  commissions: [],
  kpis: null,
  loading: false,

  fetchCommissions: async (f = {}) => {
    set({ loading: true });
    try {
      const { data } = await axiosInstance.get("/commissions", { params: f });
      set({ commissions: data.commissions || [] });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to load commissions");
    } finally {
      set({ loading: false });
    }
  },

  fetchKpis: async (f = {}) => {
    try {
      const { data } = await axiosInstance.get("/commissions/kpis", { params: f });
      set({ kpis: data.kpis });
    } catch { /* silent */ }
  },

  getCommission: async (id) => {
    try {
      const { data } = await axiosInstance.get(`/commissions/${id}`);
      return data.commission as Commission;
    } catch {
      toast.error("Failed to load commission");
      return null;
    }
  },

  processPayroll: async (id, batch) => {
    try {
      const { data } = await axiosInstance.post(`/commissions/${id}/process-payroll`, batch ? { payrollBatch: batch } : {});
      toast.success(`Payroll processed — ${data.payrollRef}${data.erpSynced ? " (ERP synced)" : ""}`);
      await Promise.all([get().fetchCommissions(), get().fetchKpis()]);
      return true;
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to process payroll");
      return false;
    }
  },
}));
